import React from 'react';
import { useDispatch } from 'react-redux';
import moment, { type Moment, type MomentInput, type weekdays } from 'moment';
import { useIntl } from 'react-intl';
import { withFormsy } from 'formsy-react';
import { IconButton, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import BaseTextField, { type BaseTextFieldProps, type FieldProps } from './components/BaseTextField';
import withFormsyValidations from './components/WithFormsyValidations';
import { parseDate, executeStored } from '../Utils';
import useSettings from '../hooks/useSettings';
import Icon from '../Icon';

const globalMinDate = '1753-01-01';

type DateFieldProps = Omit<FieldProps<string, Moment>, 'endAdornment'> & { showClosingDays?: boolean };
type DateTextFieldProps = BaseTextFieldProps<string> & { error?: boolean };

const BaseDateTextField = React.forwardRef<HTMLDivElement, DateTextFieldProps>(
	({ isValid, error, errorMessage, ...props }: DateTextFieldProps, ref) => {
		const { t } = useSettings();

		return (
			<BaseTextField
				ref={ref}
				{...props}
				isValid={!error && isValid}
				errorMessage={error && isValid ? t('WEEKDAY_NOT_ENABLED') : errorMessage}
			/>
		);
	}
);

const DateTextField = styled(BaseDateTextField)(({ isEdit, theme }) => ({
	'&.MuiTextField-root > .MuiInputBase-root': {
		paddingRight: isEdit ? 4 : 12
	}
}));

/** The date field, used to specify dates */
function DateField(props: DateFieldProps) {
	const intl = useIntl();
	const hasLogs = props.logProps?.hasLogs(props.name);
	const dispatch = useDispatch();

	const ref = React.useRef<HTMLDivElement>(null);

	const [dialogOpen, setDialogOpen] = React.useState(false);
	const switchDialogOpen = () => setDialogOpen(state => !state);

	const internalValue = React.useRef(parseDate(props.value));

	const [dates, setDates] = React.useState<any[]>([]);
	// const [currentMonth, setCurrentMonth] = React.useState(moment()); commentato per bug

	const checkIsClosingDay = (day: Moment) => {
		const result = dates.find(item => parseDate(item.date_utc)?.isSame(day));
		return !!result;
	};

	// const handleMonthChange = (month: Moment) => { commentato per bug
	// 	setCurrentMonth(month);

	// 	executeStored({
	// 		action: 'st_sys_closingdays_calc_list',
	// 		dispatch,
	// 		params: {
	// 			n_page: 1,
	// 			n_rows: 99999,
	// 			cldy_from: moment(month).startOf('month'),
	// 			cldy_to: moment(month).endOf('month')
	// 		},
	// 		successHandler: response => {
	// 			setDates(response.data || []);
	// 		}
	// 	});
	// };

	React.useEffect(() => {
		executeStored({
			action: 'st_sys_closingdays_calc_list',
			dispatch,
			params: {
				n_page: 1,
				n_rows: 99999,
				cldy_from: moment().subtract(6, 'month').startOf('month'),
				cldy_to: moment().add(6, 'month').endOf('month')
			},
			successHandler: response => {
				setDates(response.data || []);
			}
		});
	}, [dispatch]);

	function fieldParser(fieldValue: MomentInput) {
		const parsedValue = parseDate(fieldValue);
		if (parsedValue !== null) return intl.formatDate(parsedValue?.toDate());
		return null;
	}

	function handleBlur() {
		const newValue = internalValue && internalValue.current?.isValid() ? internalValue.current?.utc() : undefined;
		if (props.beforeChange) props.beforeChange(newValue);
		props.sendChange(props.name, newValue?.toISOString() || null);
		if (props.afterChange) props.afterChange(newValue);
	}

	function handleChange(newValue: Moment | null) {
		internalValue.current = newValue?.startOf('day');
		if (internalValue.current?.isSameOrAfter(globalMinDate)) {
			props.setValue(internalValue.current?.toISOString() || null);
		}
	}

	function handleDialogClose() {
		handleBlur();
		setDialogOpen(false);
	}

	return (
		<DatePicker
			ref={ref}
			className={props.className}
			name={props.name}
			// referenceDate={currentMonth} commentato per bug
			label={props.label}
			format="DD/MM/YYYY"
			formatDensity="dense"
			minDate={parseDate((props.validations as any)?.minDate)}
			maxDate={parseDate((props.validations as any)?.maxDate)}
			value={parseDate(props.value) ?? null}
			slots={{ textField: DateTextField }}
			slotProps={{
				textField: {
					name: props.name,
					isEdit: props.isEdit,
					startAdornment: props.startAdornment,
					InputProps: {
						endAdornment: props.isEdit ? (
							<IconButton size="small" onClick={switchDialogOpen}>
								<Icon icon="calendar" />
							</IconButton>
						) : (
							<></>
						)
					},
					required: props.required,
					errorMessage: props.errorMessage,
					isFormSubmitted: props.isFormSubmitted,
					isPristine: props.isPristine,
					isValid: props.isValid,
					logProps: props.logProps,
					fieldParser,
					onBlur: handleBlur
				} as BaseTextFieldProps<string>,
				popper: {
					anchorEl: ref?.current
				}
			}}
			timezone="system"
			readOnly={hasLogs}
			disabled={!props.isEdit && !hasLogs}
			onChange={handleChange}
			onAccept={handleBlur}
			open={dialogOpen}
			onClose={handleDialogClose}
			displayWeekNumber
			enableAccessibleFieldDOMStructure={false}
			// onMonthChange={handleMonthChange} commentato per bug
			shouldDisableDate={props.showClosingDays ? checkIsClosingDay : undefined}
		/>
	);
}

export default React.memo(
	withFormsyValidations(withFormsy(DateField), {
		validations: {
			minDate: globalMinDate
		},
		validationErrors: ({ t, intl }) => ({
			minDate: intl.formatMessage(
				{ id: 'dateField.minDate', defaultMessage: t('ERROR_MIN_DATE') },
				{ value: intl.formatDate(parseDate(globalMinDate)?.toDate()) }
			)
		})
	})
);
